import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, CheckCircle } from 'lucide-react';

interface ProgramEnrollment {
  id: string;
  sessions_remaining: number;
  is_active: boolean;
  enrolled_at: string;
  mentor_programs: {
    title: string;
    session_count: number;
    description: string | null;
  };
  mentors: {
    first_name: string;
    last_name: string;
  };
}

export const StudentProgramEnrollments = () => {
  const [enrollments, setEnrollments] = useState<ProgramEnrollment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase
        .from('program_enrollments')
        .select(`
          id,
          sessions_remaining,
          is_active,
          enrolled_at,
          mentor_programs (
            title,
            session_count,
            description
          ),
          mentors (
            first_name,
            last_name
          )
        `)
        .eq('student_id', session.user.id)
        .order('enrolled_at', { ascending: false });

      if (error) throw error;
      setEnrollments(data || []);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center text-muted-foreground">Loading programs...</div>;
  }

  if (enrollments.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="w-5 h-5" />
          Your Programs
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {enrollments.map((enrollment) => (
            <div
              key={enrollment.id}
              className={`border rounded-lg p-4 ${
                !enrollment.is_active ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium">{enrollment.mentor_programs.title}</h4>
                  <p className="text-sm text-muted-foreground">
                    with {enrollment.mentors.first_name} {enrollment.mentors.last_name}
                  </p>
                </div>
                {enrollment.is_active ? (
                  <Badge variant="default">Active</Badge>
                ) : (
                  <Badge variant="outline">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Completed
                  </Badge>
                )}
              </div>
              
              {enrollment.mentor_programs.description && (
                <p className="text-sm text-muted-foreground mb-3 whitespace-pre-line">
                  {enrollment.mentor_programs.description}
                </p>
              )}
              
              <div className="flex items-center gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Sessions left:</span>{' '}
                  <span className="font-medium">
                    {enrollment.sessions_remaining} of {enrollment.mentor_programs.session_count}
                  </span>
                </div>
                {enrollment.is_active && (
                  <div className="text-xs text-muted-foreground">
                    Use when booking with this mentor
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
